/**
 * SummaryCards.tsx — Reusable summary card wrapper that includes the click-to-filter behavior
 *
 * Self-contained card component with click handlers for filter toggling.
 * Generic component that can be used by any dashboard (Silver, Gold, Iberia, or other applications).
 * No dependencies on other card components - fully standalone.
 */

import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export interface SummaryCardProps {
	status: string;
	label: string;
	count: number;
	isActive: boolean;
	onClick: () => void;
	backgroundColor?: string;
	textColor?: string;
	subtitle?: string;
	isLoading?: boolean;
}

const CARD_CONTAINER_STYLES: SxProps<Theme> = {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	p: 3,
	cursor: 'pointer',
	borderRadius: '4px',
	transition: 'all 0.3s ease',
	position: 'relative',
	gap: 0.75,
	minHeight: '140px',
	width: '220px',
	textAlign: 'center',
};

const CARD_WRAPPER_STYLES: SxProps<Theme> = {
	cursor: 'pointer',
	flex: '0 0 auto',
	minWidth: '220px',
	transition: 'all 0.2s ease',
	position: 'relative',
	display: 'flex',
	height: 'fit-content',
	'&:hover': {
		transform: 'translateY(-4px)',
	},
};

const ACTIVE_CARD_STYLES: SxProps<Theme> = {
	outline: '3px solid #36A8FF',
	outlineOffset: '-3px',
	borderRadius: '8px',
	boxShadow: '0 4px 12px rgba(54, 168, 255, 0.25)',
};

/**
 * A summary card that filters the table when clicked.
 * Standalone card component with outline focus indicator.
 */
export const SummaryCard: React.FC<SummaryCardProps> = ({
	status,
	label,
	count,
	isActive,
	onClick,
	backgroundColor = '#003771',
	textColor = '#ffffff',
	subtitle,
	isLoading = false,
}) => {
	const [isHovered, setIsHovered] = React.useState(false);

	const bgColor = isHovered ? textColor : backgroundColor;
	const computedTextColor = isHovered ? backgroundColor : textColor;

	return (
		<Box
			onClick={onClick}
			sx={{
				...CARD_WRAPPER_STYLES,
				...(isActive && ACTIVE_CARD_STYLES),
			}}
		>
			<Box
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				sx={{
					...CARD_CONTAINER_STYLES,
					backgroundColor: bgColor,
					boxShadow: isHovered ? '0 8px 24px rgba(0, 55, 113, 0.25)' : '0 2px 8px rgba(0, 55, 113, 0.12)',
				}}
			>
				{/* Title */}
				<Typography
					variant="body1"
					sx={{
						fontWeight: 600,
						lineHeight: 1.2,
						fontSize: '0.875rem',
						color: computedTextColor,
						wordBreak: 'break-word',
						textAlign: 'center',
						px: 2.5,
					}}
				>
					{label}
				</Typography>

				{/* Large Value or Loading Spinner */}
				{isLoading ? (
					<CircularProgress
						size={40}
						thickness={4}
						sx={{
							color: computedTextColor,
						}}
					/>
				) : (
					<Typography
						variant="h2"
						sx={{
							fontWeight: 700,
							fontSize: '2rem',
							lineHeight: 1,
							color: computedTextColor,
							textAlign: 'center',
						}}
					>
						{count}
					</Typography>
				)}

				{/* Subtitle */}
				{subtitle && (
					<Typography
						variant="body2"
						sx={{
							fontSize: '0.85rem',
							opacity: 0.85,
							fontWeight: 500,
							color: computedTextColor,
						}}
					>
						{subtitle}
					</Typography>
				)}
			</Box>
		</Box>
	);
};

export interface SummaryCardGroupProps {
	cards: SummaryCardProps[];
	containerProps?: {
		sx?: SxProps<Theme>;
		[key: string]: any;
	};
}

/**
 * A group of summary cards, laid out horizontally with wrapping.
 */
export const SummaryCardGroup: React.FC<SummaryCardGroupProps> = ({
	cards,
	containerProps
}) => {
	return (
		<Box
			sx={{
				display: 'flex',
				gap: 2,
				flexWrap: 'wrap',
				alignItems: 'stretch',
				...containerProps?.sx
			}}
			{...containerProps}
		>
			{cards.map((card) => (
				<Box key={card.status} sx={{ flex: '1 1 auto', minWidth: '140px' }}>
					<SummaryCard {...card} />
				</Box>
			))}
		</Box>
	);
};
